import type { MappedData } from "../types/types";

type AnyObject = Record<string, any>;

export function mapList(
  list: any[],
  labelField: string | string[],
  idField: string,
  associatedField?: string,
  fatherIdField?: string
) {
  return list?.map((item) => {
    const label = Array.isArray(labelField)
      ? labelField.map((field) => item[field]).join(' ')
      : item[labelField];

    return {
      value: item[idField],
      label,
      id: item[idField],
      fatherId: item[fatherIdField!] ?? null,
      associatedField,
      valueField: labelField,
    };
  });
}

export function mapWithReferences(
  list: any[],
  mappings: {
    fieldToMatch: string; // Campo en la lista principal (como codComunidad o codPais)
    fieldToAdd: string; // Nombre del campo nuevo a agregar (como comunidad o pais)
    referenceList: any[]; // Lista mapeada (como comunidadesMapped o paisesMapped)
    referenceKey: string; // Clave en la lista mapeada para comparar (como id)
    referenceValue: string; // Valor en la lista mapeada a agregar (como value)
  }[]
) {
  return list.map((item) => {
    const newItem = { ...item };
    mappings?.forEach(
      ({
        fieldToMatch,
        fieldToAdd,
        referenceList,
        referenceKey,
        referenceValue,
      }) => {
        const reference = referenceList.find(
          (ref) => ref[referenceKey] === item[fieldToMatch]
        );
        newItem[fieldToAdd] = reference ? reference[referenceValue] : null;
      }
    );
    return newItem;
  });
}

export function extractProperties<T extends AnyObject>(
  data: T | null | undefined,
  keys: (keyof T | string)[],
  labels: string[]
): Record<string, any> {
  if (keys.length !== labels.length) {
    throw new Error("El número de claves y etiquetas debe coincidir.");
  }

  // Si data es nulo o no es objeto, devolvemos objeto vacío
  if (data == null || typeof data !== "object") {
    return {};
  }

  return keys.reduce((result, key, index) => {
    const label = labels[index];

    if (typeof key === "string" && key.includes("/")) {
      // Manejo de rutas anidadas ("parent/child")
      const [parentKey, childKey] = key.split("/");
      const parentVal = (data as any)[parentKey];

      if (
        parentVal != null &&
        typeof parentVal === "object" &&
        childKey in parentVal
      ) {
        result[label] = (parentVal as Record<string, any>)[childKey];
      }
    } else {
      // Sólo accedemos si data sigue siendo objeto
      if (typeof data === "object" && key in data) {
        result[label] = (data as Record<string, any>)[key as string];
      }
    }

    return result;
  }, {} as Record<string, any>);
}

export function mappedDataToFormData(mappedData: MappedData): FormData {
  const formData = new FormData();
  for (const key in mappedData) {
    if (!Object.prototype.hasOwnProperty.call(mappedData, key)) continue;
    const value = mappedData[key];

    if (value instanceof File || value instanceof Blob) {
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      value.forEach((item, idx) => {
        if (item instanceof File || item instanceof Blob) {
          formData.append(`${key}[${idx}]`, item);
        } else {
          formData.append(`${key}[${idx}]`, String(item));
        }
      });
    } else if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  }
  return formData;
}

// Función auxiliar para detectar si hay archivos en mappedData
// export function hasFileInData(data: MappedData): boolean {
//   return Object.values(data).some(
//     (v) =>
//       v instanceof File ||
//       v instanceof Blob ||
//       (Array.isArray(v) &&
//         v.some((item) => item instanceof File || item instanceof Blob))
//   );
// }
export function hasFileInData(data: FormData | Record<string, any>): boolean {
  if (data instanceof FormData) {
    let hasFile = false;
    Array.from(data.entries()).forEach(([key, value]) => {
      if ((value as any) instanceof File || (value as any) instanceof Blob) {
        hasFile = true;
      }
    });
    return hasFile;
  }

  if (typeof data === 'object' && data !== null) {
    for (const key in data) {
      if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
      const value = (data as any)[key];
      if (value instanceof File || value instanceof Blob) {
        return true;
      }
      if (Array.isArray(value)) {
        for (const el of value) {
          if (el instanceof File || el instanceof Blob) {
            return true;
          }
        }
      }
      if (value && typeof value === 'object' && !(value instanceof Date)) {
        if (hasFileInData(value)) {
          return true;
        }
      }
    }
  }

  return false;
}

export function sortByProperty<T, K extends keyof T>(
  array: T[],
  property: K
): T[] {
  return [...array].sort((a, b) => {
    const valA = a[property];
    const valB = b[property];
    if (typeof valA === "number" && typeof valB === "number") {
      return valA - valB;
    }
    if (typeof valA === "string" && typeof valB === "string") {
      return valA.localeCompare(valB);
    }
    return 0;
  });
}

    // Función para formatear fechas a DD/MM/AAAA
    export function formatDate (dateString:string) : string  {
        if (!dateString) return "N/A";

        try {
            const date = new Date(dateString);
            const day = date.getDate().toString().padStart(2, "0");
            const month = (date.getMonth() + 1).toString().padStart(2, "0");
            const year = date.getFullYear();

            return `${day}/${month}/${year}`;
        } catch (error) {
            return dateString;
        }
    }

    // Constantes para formateo en español
export const MESES_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
export const DIAS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

/**
 * Obtiene el número de semana ISO 8601.
 * Las semanas empiezan en lunes.
 * @param {Date} d - La fecha de entrada.
 * @returns {number} El número de la semana.
 */
export function getWeekNumber(d: Date): number {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1)); // Esto es un objeto Date
    const diff = d.getTime() - yearStart.getTime(); // Obtener la diferencia en milisegundos
    const weekNo = Math.ceil(((diff / 86400000) + 1) / 7);
    return weekNo;
}

/**
 * Obtiene el primer día (lunes) de la semana para una fecha dada.
 * @param {Date} d - La fecha de entrada.
 * @returns {Date} La fecha del lunes de esa semana.
 */
export function getStartOfWeek(d: Date | string): Date {
    const date = new Date(d);
    const day = date.getDay(); // 0 para Domingo, 1 para Lunes...
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // ajusta para que el Lunes sea el primer día
    return new Date(date.setDate(diff));
}

/**
 * Calcula y formatea el tiempo transcurrido desde una fecha dada de forma concisa.
 * @param fecha La fecha de inicio, puede ser un objeto Date, un string o un timestamp.
 * @returns Un string como "Ahora mismo", "Hace 5m", "Hace 3h", "Hace 2d", etc.
 */
export const tiempoDesde = (fecha: Date | string | number): string => {
  const fechaPasada = new Date(fecha);
  const ahora = new Date();

  const segundosTranscurridos = Math.floor((ahora.getTime() - fechaPasada.getTime()) / 1000);

  const minuto = 60;
  const hora = minuto * 60;
  const dia = hora * 24;
  const semana = dia * 7;
  const mes = dia * 30;
  const anio = dia * 365;

  if (segundosTranscurridos >= anio) {
    const cantidad = Math.floor(segundosTranscurridos / anio);
    return `Hace ${cantidad}a`;
  }
  if (segundosTranscurridos >= mes) {
    const cantidad = Math.floor(segundosTranscurridos / mes);
    return `Hace ${cantidad}mes`;
  }
  if (segundosTranscurridos >= semana) {
    const cantidad = Math.floor(segundosTranscurridos / semana);
    return `Hace ${cantidad}sem`;
  }
  if (segundosTranscurridos >= dia) {
    const cantidad = Math.floor(segundosTranscurridos / dia);
    return `Hace ${cantidad}d`;
  }
  if (segundosTranscurridos >= hora) {
    const cantidad = Math.floor(segundosTranscurridos / hora);
    return `Hace ${cantidad}h`;
  }
  if (segundosTranscurridos >= minuto) {
    const cantidad = Math.floor(segundosTranscurridos / minuto);
    return `Hace ${cantidad}m`;
  }
  
  if (segundosTranscurridos < 10) {
    return "Ahora mismo";
  }

  return `Hace ${segundosTranscurridos}s`;
};